import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {
    contentMaxWidth,
    colors,
    fontSize,
    fontWeight,
    spacing,
    lineHeight,
    letterSpacing,
    borderRadius,
    overlay,
    borderWidth
} from '@/styles/tokens';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import {cdnUrl} from '@/util/cdnImage';
import {fadeInUp} from '@/animation/scroll-animation';
import {AnimatedComponent} from '../AnimatedComponent';
import type {technicalArchContent} from '@/util/contents/drawpost-data';

interface TechnicalArchitectureProps {
    heading: string;
    content: typeof technicalArchContent;
}

const sectionSx = {
    bgcolor: colors.background.slate50
} as const;

const cardSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.lg,
    p: spacing[20],
    mb: spacing[16]
} as const;

const cardTitleSx = {
    color: colors.text.primary,
    fontSize: fontSize[20],
    lineHeight: lineHeight[28],
    letterSpacing: letterSpacing.snug,
    mb: spacing[12]
} as const;

const cardBodySx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[24],
    letterSpacing: letterSpacing.normal,
    mb: spacing[16]
} as const;

const checklistItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[8],
    mb: 1
} as const;

const checkIconSx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    flexShrink: 0,
    mt: spacing[4]
} as const;

const checkTextSx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[20],
    letterSpacing: letterSpacing.normal
} as const;

const rightColSx = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    mt: {xs: 4, md: 0}
} as const;

const architecturePanelSx = {
    width: '100%',
    maxWidth: {md: 440},
    bgcolor: colors.accent.indigo,
    border: `${borderWidth.thin} solid ${overlay.whiteTenth}`,
    borderRadius: borderRadius.xl,
    p: spacing[24],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing[16]
} as const;

const layerCardSx = {
    bgcolor: overlay.whiteTenth,
    borderRadius: borderRadius.md,
    px: spacing[16],
    py: spacing[16],
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[12]
} as const;

const layerIconBoxSx = {
    width: spacing[20],
    height: spacing[20],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mt: spacing[4]
} as const;

const layerLabelSx = {
    color: colors.text.inverse,
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight[20],
    letterSpacing: letterSpacing.normal
} as const;

const layerDescSx = {
    color: overlay.whiteSubtitle,
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[20],
    letterSpacing: letterSpacing.normal,
    mb: spacing[8]
} as const;

const layerTechRowSx = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing[8]
} as const;

const techPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: spacing[12],
    py: spacing[4],
    borderRadius: borderRadius.full,
    bgcolor: colors.accent.indigo,
    color: colors.text.inverse,
    fontSize: fontSize[12],
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[20],
    letterSpacing: letterSpacing.normal
} as const;

const techPlusSx = {
    color: overlay.whiteSubtitle,
    fontSize: fontSize[12],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[20]
} as const;

const layerTextWrapSx = {
    minWidth: 0
} as const;

const connectorSx = {
    width: borderWidth.thick,
    height: spacing[16],
    bgcolor: colors.text.inverse,
    mx: 'auto'
} as const;

const TechnicalArchitecture = memo(function TechnicalArchitecture({heading, content}: TechnicalArchitectureProps) {
    return (
        <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom" sx={sectionSx}>
            <MUIContainer>
                <Box textAlign="center" maxWidth={contentMaxWidth.standard} margin="0 auto" mb={5}>
                    <Typography
                        variant="h1"
                        component="h2"
                        fontSize={{xs: 35, sm: 48}}
                        className="font-headers"
                        sx={{color: colors.text.primary}}
                    >
                        {heading}
                    </Typography>
                </Box>

                <Grid container spacing={{xs: 4, md: 6}} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                        {content.cards.map((card, cardIndex) => (
                            <AnimatedComponent key={cardIndex} variants={fadeInUp} delay={cardIndex * 0.1}>
                                <Box sx={cardSx}>
                                    <Typography component="h3" sx={cardTitleSx} className="font-headers">
                                        {card.title}
                                    </Typography>
                                    <Typography sx={cardBodySx} className="font-regular-text remove-underline">
                                        {card.body}
                                    </Typography>
                                    <Box>
                                        {card.bullets.map((bullet, bulletIndex) => (
                                            <Box key={bulletIndex} sx={checklistItemSx}>
                                                <CheckCircleOutlineRounded sx={checkIconSx} />
                                                <Typography sx={checkTextSx} className="font-regular-text remove-underline">
                                                    {bullet}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    {'note' in card && card.note ? (
                                        <Typography sx={cardBodySx} className="font-regular-text remove-underline">
                                            {card.note}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </AnimatedComponent>
                        ))}
                    </Grid>

                    <Grid item xs={12} md={4} sx={rightColSx}>
                        <Box sx={architecturePanelSx}>
                            {content.layers.map((layer, index) => (
                                <React.Fragment key={index}>
                                    <Box sx={layerCardSx}>
                                        <Box sx={layerIconBoxSx}>
                                            <Image
                                                src={cdnUrl(layer.iconSrc)}
                                                alt=""
                                                aria-hidden="true"
                                                width={20}
                                                height={20}
                                                style={{filter: 'brightness(0) invert(1)'}}
                                            />
                                        </Box>
                                        <Box sx={layerTextWrapSx}>
                                            <Typography sx={layerLabelSx}>{layer.label}</Typography>
                                            <Typography sx={layerDescSx}>{layer.description}</Typography>
                                            <Box sx={layerTechRowSx}>
                                                {layer.tech.map((tech, techIndex) => (
                                                    <React.Fragment key={techIndex}>
                                                        {techIndex > 0 && (
                                                            <Box component="span" sx={techPlusSx}>
                                                                +
                                                            </Box>
                                                        )}
                                                        <Box component="span" sx={techPillSx} className="font-headers">
                                                            {tech}
                                                        </Box>
                                                    </React.Fragment>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                    {index < content.layers.length - 1 && <Box sx={connectorSx} />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
});

export default TechnicalArchitecture;
